import type { ChatMessage, ChatStreamEvent, RiskLevel } from '@app/shared';

/**
 * The quick-chat transcript reducer (§21, WP-C4). Unlike the Operations stream —
 * which only invalidates queries so a screen refetches canonical state — a chat turn
 * streams actual CONTENT, so the panel IS the live view of the turn. This pure
 * reducer folds the `chatStreamEvent` discriminated union (plus a few local UI
 * actions: sending a message, deciding an approval, a transport error) into an
 * ordered list of render-ready transcript items.
 *
 * It is deliberately framework-free and deterministic — synthesized item ids come
 * from a monotonic `seq`, not `crypto.randomUUID()` — so a scripted event sequence
 * can be asserted directly in a `node` unit test (the component/e2e render is WP-C5).
 */

export type ChatApprovalDecision = 'approve' | 'reject';
export type TurnStatus = 'completed' | 'failed' | 'limit_reached';
export type ToolChipStatus = 'running' | 'ok' | 'error';

/** One render-ready row of the transcript, in causal order. */
export type TranscriptItem =
  | { kind: 'user'; id: string; content: string }
  | {
      kind: 'assistant';
      id: string;
      content: string;
      /** True while `model_delta`s are still appending to this bubble. */
      streaming: boolean;
      /**
       * The model's extended-reasoning summary for this turn segment (§21, v1.11),
       * folded from `thinking` events that precede the bubble's text. Rendered as a
       * collapsible "Thinking" section above the bubble (like Claude's "Thought for…").
       */
      reasoning?: string;
      /** Set on `turn_done`/transport error — drives the terminal-state render. */
      status?: TurnStatus;
      error?: string;
    }
  | {
      kind: 'tool_call';
      id: string;
      tool: string;
      arguments: Record<string, unknown>;
      status: ToolChipStatus;
      /** Present when the tool finished with `{ ok: false }`. */
      error?: string;
    }
  | {
      kind: 'approval';
      id: string;
      approvalId: string;
      tool: string;
      reason: string;
      risk: RiskLevel;
      /** Undefined until the user acts; set (and buttons disabled) after. */
      decision?: ChatApprovalDecision;
    };

export interface TranscriptState {
  items: TranscriptItem[];
  /** Captured from the first streamed event (or history) — carries the conversation. */
  sessionId: string | null;
  /** True while a turn is in flight (input + send disabled). */
  streaming: boolean;
  /** Monotonic counter for deterministic synthesized item ids. */
  seq: number;
}

export type TranscriptAction =
  | { type: 'reset' }
  | { type: 'load_history'; sessionId: string; messages: ChatMessage[] }
  | { type: 'send'; id: string; content: string }
  | { type: 'stream_event'; event: ChatStreamEvent }
  | { type: 'stream_error'; error: string }
  | { type: 'approval_decided'; approvalId: string; decision: ChatApprovalDecision }
  | { type: 'approval_reset'; approvalId: string };

export const initialTranscriptState: TranscriptState = {
  items: [],
  sessionId: null,
  streaming: false,
  seq: 0,
};

/** The last item, iff it is an assistant bubble still accepting deltas. */
function openAssistantIndex(items: TranscriptItem[]): number {
  const last = items[items.length - 1];
  return last && last.kind === 'assistant' && last.streaming ? items.length - 1 : -1;
}

function finalizeStreamingBubble(
  items: TranscriptItem[],
  status: TurnStatus,
  error?: string,
): TranscriptItem[] {
  // Stop every open bubble, but pin the terminal status/error to only the LAST one
  // (a turn can produce several bubbles around tool calls — the status is the turn's,
  // shown once).
  let lastStreaming = -1;
  for (let i = items.length - 1; i >= 0; i -= 1) {
    if (items[i]!.kind === 'assistant' && (items[i] as { streaming: boolean }).streaming) {
      lastStreaming = i;
      break;
    }
  }
  return items.map((item, i) =>
    item.kind === 'assistant' && item.streaming
      ? i === lastStreaming
        ? { ...item, streaming: false, status, error: error ?? item.error }
        : { ...item, streaming: false }
      : item,
  );
}

function foldEvent(state: TranscriptState, event: ChatStreamEvent): TranscriptState {
  // Every event carries the (possibly newly minted) sessionId — capture it so the
  // next turn continues the same conversation.
  const sessionId = event.sessionId;

  switch (event.kind) {
    case 'thinking': {
      // A reasoning summary arrives BEFORE that turn segment's text (§21, v1.11). Attach
      // it to the open assistant bubble if one is accepting deltas; otherwise open a new
      // (empty, still-streaming) bubble to carry it, so the following `model_delta`s
      // append their text to the SAME bubble. Ignore an empty summary.
      if (!event.text) return { ...state, sessionId };
      const idx = openAssistantIndex(state.items);
      if (idx >= 0) {
        const items = state.items.slice();
        const bubble = items[idx] as Extract<TranscriptItem, { kind: 'assistant' }>;
        items[idx] = {
          ...bubble,
          reasoning: (bubble.reasoning ?? '') + event.text,
        };
        return { ...state, items, sessionId };
      }
      return {
        ...state,
        sessionId,
        seq: state.seq + 1,
        items: [
          ...state.items,
          {
            kind: 'assistant',
            id: `a-${state.seq}`,
            content: '',
            streaming: true,
            reasoning: event.text,
          },
        ],
      };
    }

    case 'model_delta': {
      const idx = openAssistantIndex(state.items);
      if (idx >= 0) {
        const items = state.items.slice();
        const bubble = items[idx] as Extract<TranscriptItem, { kind: 'assistant' }>;
        items[idx] = { ...bubble, content: bubble.content + event.text };
        return { ...state, items, sessionId };
      }
      return {
        ...state,
        sessionId,
        seq: state.seq + 1,
        items: [
          ...state.items,
          { kind: 'assistant', id: `a-${state.seq}`, content: event.text, streaming: true },
        ],
      };
    }

    case 'tool_call_started':
      return {
        ...state,
        sessionId,
        seq: state.seq + 1,
        items: [
          ...state.items,
          {
            kind: 'tool_call',
            id: `t-${state.seq}`,
            tool: event.tool,
            arguments: event.arguments,
            status: 'running',
          },
        ],
      };

    case 'tool_call_finished': {
      // Resolve the most recent still-running chip for this tool.
      let target = -1;
      for (let i = state.items.length - 1; i >= 0; i -= 1) {
        const it = state.items[i]!;
        if (it.kind === 'tool_call' && it.tool === event.tool && it.status === 'running') {
          target = i;
          break;
        }
      }
      if (target < 0) return { ...state, sessionId };
      const items = state.items.slice();
      const chip = items[target] as Extract<TranscriptItem, { kind: 'tool_call' }>;
      items[target] = event.result.ok
        ? { ...chip, status: 'ok' }
        : { ...chip, status: 'error', error: event.result.error.message };
      return { ...state, items, sessionId };
    }

    case 'approval_required':
      return {
        ...state,
        sessionId,
        seq: state.seq + 1,
        items: [
          ...state.items,
          {
            kind: 'approval',
            id: `ap-${state.seq}`,
            approvalId: event.approvalId,
            tool: event.tool,
            reason: event.reason,
            risk: event.risk,
          },
        ],
      };

    case 'turn_done': {
      const idx = openAssistantIndex(state.items);
      if (idx >= 0) {
        return {
          ...state,
          sessionId,
          streaming: false,
          items: finalizeStreamingBubble(state.items, event.status, event.error),
        };
      }
      // No open bubble (e.g. failed/limit before any text) — surface the terminal
      // state as its own assistant row so it never renders silently. A clean
      // `completed` with no bubble carries nothing to show.
      if (event.status === 'completed') {
        return { ...state, sessionId, streaming: false };
      }
      return {
        ...state,
        sessionId,
        streaming: false,
        seq: state.seq + 1,
        items: [
          ...state.items,
          {
            kind: 'assistant',
            id: `a-${state.seq}`,
            content: '',
            streaming: false,
            status: event.status,
            error: event.error,
          },
        ],
      };
    }

    default:
      return state;
  }
}

export function transcriptReducer(
  state: TranscriptState,
  action: TranscriptAction,
): TranscriptState {
  switch (action.type) {
    case 'reset':
      return initialTranscriptState;

    case 'load_history':
      return {
        items: action.messages.map((m) =>
          m.role === 'user'
            ? { kind: 'user', id: m.id, content: m.content }
            : { kind: 'assistant', id: m.id, content: m.content, streaming: false },
        ),
        sessionId: action.sessionId,
        streaming: false,
        seq: 0,
      };

    case 'send':
      return {
        ...state,
        streaming: true,
        items: [...state.items, { kind: 'user', id: action.id, content: action.content }],
      };

    case 'stream_event':
      return foldEvent(state, action.event);

    case 'stream_error': {
      const idx = openAssistantIndex(state.items);
      if (idx >= 0) {
        return {
          ...state,
          streaming: false,
          items: finalizeStreamingBubble(state.items, 'failed', action.error),
        };
      }
      return {
        ...state,
        streaming: false,
        seq: state.seq + 1,
        items: [
          ...state.items,
          {
            kind: 'assistant',
            id: `a-${state.seq}`,
            content: '',
            streaming: false,
            status: 'failed',
            error: action.error,
          },
        ],
      };
    }

    case 'approval_decided':
      return {
        ...state,
        items: state.items.map((item) =>
          item.kind === 'approval' && item.approvalId === action.approvalId
            ? { ...item, decision: action.decision }
            : item,
        ),
      };

    case 'approval_reset':
      return {
        ...state,
        items: state.items.map((item) =>
          item.kind === 'approval' && item.approvalId === action.approvalId
            ? { ...item, decision: undefined }
            : item,
        ),
      };

    default:
      return state;
  }
}

/**
 * Whether to show the live "Thinking…" pulse (§21, v1.11, Part A). The panel shows an
 * animated indicator while a turn is in flight and the model has gone quiet — i.e. no
 * assistant text / running tool / pending approval is currently on screen to signal
 * activity. Driven purely off the streaming flag + the transcript tail, so it appears
 * after the user's message until the first output, and again between tool calls.
 */
export function isThinking(state: TranscriptState): boolean {
  if (!state.streaming) return false;
  const last = state.items[state.items.length - 1];
  if (!last) return true;
  switch (last.kind) {
    case 'user':
      // Sent, waiting for the first model output.
      return true;
    case 'assistant':
      // A bubble with no text yet (e.g. a reasoning-only segment) is still "thinking";
      // once `model_delta`s land the bubble itself signals the activity.
      return last.content.length === 0;
    case 'tool_call':
      // A running call spins its own chip; a finished one means the model went quiet.
      return last.status !== 'running';
    case 'approval':
      // A pending approval shows its card; a decided one waits on the model to resume.
      return last.decision != null;
    default:
      return false;
  }
}

/**
 * Serialize the current transcript to a simple Markdown document (§21, v1.11). Pure +
 * deterministic so it is unit-testable and can back a client-only "Export conversation"
 * download. Each turn becomes a `**You:**` / `**Assistant:**` block; tool calls render
 * as a compact `> 🔧 called \`tool\`` line and a reasoning summary as a
 * `> _Thinking:_ …` blockquote, so the export reflects everything the panel showed.
 */
export function transcriptToMarkdown(
  items: TranscriptItem[],
  opts: { sessionId?: string | null; date?: string } = {},
): string {
  const lines: string[] = ['# Assistant conversation'];
  if (opts.sessionId) lines.push(`Session: ${opts.sessionId}`);
  if (opts.date) lines.push(`Exported: ${opts.date}`);
  lines.push('');

  for (const item of items) {
    switch (item.kind) {
      case 'user':
        lines.push(`**You:** ${item.content}`.trimEnd(), '');
        break;
      case 'assistant': {
        if (item.reasoning) {
          for (const rl of item.reasoning.split('\n')) lines.push(`> _Thinking:_ ${rl}`.trimEnd());
        }
        const body = item.content.trim();
        lines.push(`**Assistant:** ${body}`.trimEnd(), '');
        break;
      }
      case 'tool_call':
        lines.push(`> 🔧 called \`${item.tool}\``, '');
        break;
      case 'approval':
        lines.push(`> ⏸️ approval requested for \`${item.tool}\``, '');
        break;
    }
  }

  // Collapse the trailing blank line into a single terminating newline.
  return `${lines.join('\n').trimEnd()}\n`;
}
